import ErrorPage from "@/components/pages/ErrorPage";
import { AnalyticsBuilderPanel } from "@/features/analytics/builder/AnalyticsBuilderPanel";
import { useAnalyticsCopy } from "@/features/i18n/analyticsCopy";
import {
    clearDashboardConfig,
    columnOverridesFromRawHide,
    normalizeDashboardConfig,
} from "@/features/analytics/builder/engine/dashboardConfigApi";
import { ANALYTICS_DASHBOARD_CONFIG_QUERY_KEY } from "@/features/analytics/builder/engine/dashboardConfigPersist";
import { parseDashboardConfigFromSearch } from "@/features/analytics/builder/engine/dashboardConfigSchema";
import { createInitialDashboardConfig } from "@/features/analytics/builder/engine/dummyData";
import type { DashboardConfig, ViewConfig } from "@/features/analytics/builder/engine/types";
import { toast } from "@patient-watch/ui/sonner";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { z } from "@/utils/form";
import { useEffect, useMemo, useRef } from "react";

const analyticsSearchSchema = z.object({
    /** JSON-encoded DashboardConfig - lets any arrangement of views be shared/bookmarked via
     * URL. TanStack's default search parser may deserialize that JSON before validation, so both
     * representations must be accepted. Malformed values degrade to undefined instead of
     * erroring the route. */
    config: z
        .union([z.string(), z.record(z.unknown())])
        .optional()
        .catch(undefined),
    /** Comma-separated raw-data column keys to hide (bookmarkable column visibility). */
    rawHide: z.string().optional().catch(undefined),
});

/** Starter tabs: Events / Outcomes / Raw data. Study tabs are added at runtime when relevant.
 * Persisted per user in analytics_dashboard_configs (see seed). */
const DEFAULT_ANALYTICS_VIEWS: ViewConfig[] = createInitialDashboardConfig().views;

function isDataView(view: ViewConfig | undefined): boolean {
    if (!view) return false;
    return view.kind === "data" || view.system === "raw-data" || Boolean(view.locked && !view.system);
}

function AnalyticsErrorPage({ error, reset }: { error: Error; reset: () => void }) {
    const copy = useAnalyticsCopy();
    const router = useRouter();
    const queryClient = useQueryClient();

    const handleResetLayout = async () => {
        try {
            await clearDashboardConfig();
            await queryClient.invalidateQueries({ queryKey: ANALYTICS_DASHBOARD_CONFIG_QUERY_KEY });
            await router.navigate({ to: "/analytics", search: {}, replace: true });
            reset();
            toast.success("Analytics layout reset to defaults");
        } catch (resetError) {
            const message = resetError instanceof Error ? resetError.message : "Could not reset analytics layout";
            toast.error(message);
        }
    };

    return (
        <div className="space-y-4">
            <ErrorPage title={copy.failedToLoadAnalytics} error={error} reset={reset} />
            <div className="flex justify-center px-6 pb-8">
                <button
                    type="button"
                    className="text-indigo-600 hover:text-indigo-700 text-sm underline"
                    onClick={() => void handleResetLayout()}
                >
                    Reset saved layout and try again
                </button>
            </div>
        </div>
    );
}

/** The single analytics page: every diary the user can access, arranged into per-user saved
 * views (Notion-style tabs with their own filters/widgets). Config lives in the URL and in
 * analytics_dashboard_configs - there are no per-cohort analytics subroutes any more. */
const AnalyticsPage = () => {
    const { config: configParam, rawHide } = Route.useSearch();
    const navigate = Route.useNavigate();
    const rawHideMigrated = useRef(false);

    const searchConfig = useMemo(() => parseDashboardConfigFromSearch(configParam), [configParam]);

    useEffect(() => {
        if (rawHideMigrated.current || !rawHide) return;
        rawHideMigrated.current = true;

        const baseConfig = normalizeDashboardConfig(
            searchConfig ?? {
                activeViewId: DEFAULT_ANALYTICS_VIEWS[0]?.id ?? "",
                views: DEFAULT_ANALYTICS_VIEWS,
            }
        );
        const activeView = baseConfig.views.find((view) => view.id === baseConfig.activeViewId) ?? baseConfig.views[0];
        const targetView =
            activeView && isDataView(activeView) ? activeView : (baseConfig.views.find(isDataView) ?? activeView);
        if (!targetView) return;

        const overrides = columnOverridesFromRawHide(rawHide);
        const updatedConfig: DashboardConfig = {
            ...baseConfig,
            views: baseConfig.views.map((view) =>
                view.id === targetView.id
                    ? {
                          ...view,
                          columnOverrides: { ...view.columnOverrides, ...overrides },
                      }
                    : view
            ),
        };

        void navigate({
            search: (prev) => ({
                ...prev,
                config: JSON.stringify(updatedConfig),
                rawHide: undefined,
            }),
            replace: true,
        });
    }, [rawHide, searchConfig, navigate]);

    const handleConfigChange = (config: DashboardConfig) => {
        void navigate({
            search: (prev) => ({ ...prev, config: JSON.stringify(config) }),
            replace: true,
        });
    };

    return (
        <div className="mb-9">
            <AnalyticsBuilderPanel
                searchConfig={searchConfig ?? undefined}
                onConfigChange={handleConfigChange}
                initialViews={DEFAULT_ANALYTICS_VIEWS}
            />
        </div>
    );
};

export const Route = createFileRoute("/_app/analytics/")({
    component: AnalyticsPage,
    errorComponent: AnalyticsErrorPage,
    validateSearch: (search) => analyticsSearchSchema.parse(search),
    beforeLoad: ({ context }) => {
        if (!context.auth?.id) throw redirect({ to: "/login" });
    },
});
